import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/** Merge Tailwind classes safely */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Nome reale "Nome Cognome". */
export function realName(op: { firstName?: string; lastName?: string }): string {
  return `${op.firstName ?? ''} ${op.lastName ?? ''}`.trim()
}

/** Nome pubblico operatore: displayName/nickname dal backend, altrimenti nome reale. */
export function operatorDisplayName(op: {
  displayName?: string; nickname?: string; firstName?: string; lastName?: string
}): string {
  return (op.displayName || op.nickname || '').trim() || realName(op)
}

/** true se l'operatore mostra un nome pubblico diverso dal nome reale. */
export function operatorHasAlias(op: {
  displayName?: string; nickname?: string; firstName?: string; lastName?: string
}): boolean {
  return operatorDisplayName(op) !== realName(op)
}

/**
 * Ammorbidisce un testo scritto TUTTO MAIUSCOLO portandolo a "sentence case":
 * minuscolo + iniziale maiuscola a inizio testo/paragrafo e dopo `.`, `!`, `?`.
 * Se il testo NON è prevalentemente maiuscolo (es. già scritto normalmente)
 * viene lasciato invariato, così non si rovina chi scrive correttamente.
 * Usato a display-time su nome/descrizione delle prestazioni.
 */
export function softenShoutyText(text?: string | null): string {
  const s = (text ?? '').trim()
  if (!s) return ''
  const letters = s.match(/\p{L}/gu) ?? []
  if (letters.length < 4) return fixBrandCase(s) // troppo corto per giudicare (sigle, ecc.)
  const uppers = s.match(/\p{Lu}/gu) ?? []
  if (uppers.length / letters.length < 0.7) return fixBrandCase(s) // testo normale: non toccare
  const lowered = s.toLowerCase()
  // Maiuscola a inizio stringa, dopo punteggiatura di fine frase e a capo.
  const sentenced = lowered.replace(
    /(^\s*|[.!?]\s+|\n\s*)(\p{Ll})/gu,
    (_m, prefix: string, ch: string) => prefix + ch.toUpperCase(),
  )
  return fixBrandCase(sentenced)
}

// Parole funzionali che restano minuscole nel de-shout (tranne a inizio stringa).
const DESHOUT_STOPWORDS = new Set([
  'di', 'a', 'da', 'in', 'con', 'su', 'per', 'tra', 'fra', 'e', 'ed', 'o', 'od',
  'il', 'lo', 'la', 'i', 'gli', 'le', 'un', 'uno', 'una', 'al', 'allo', 'alla',
  'ai', 'agli', 'alle', 'del', 'dello', 'della', 'dei', 'degli', 'delle',
  'dal', 'dallo', 'dalla', 'dai', 'dagli', 'dalle', 'nel', 'nello', 'nella',
  'nei', 'negli', 'nelle', 'sul', 'sullo', 'sulla', 'sui', 'sugli', 'sulle',
  'd', 'l', 'all', 'dell', 'nell', 'sull', 'the', 'of', 'and',
])

/**
 * De-shout per TITOLI e NOMI (Title Case parola per parola), complementare a
 * `softenShoutyText` che è pensata per la prosa (sentence case).
 * «FORMAZIONE TAI TECNICHE DI ALCHIMIA» → «Formazione TAI Tecniche di Alchimia».
 * Sistema anche le stringhe miste («RESPIRO CONSAPEVOLE Barbara Ruggiero»);
 * le parole già in casing misto e le sigle ≤3 lettere restano intatte.
 * Mirror di `_deshout` in lambda/ollylife-manager/routes/operators.py.
 */
export function deshout(s?: string | null): string {
  if (!s) return ''
  const fixPart = (part: string, isFirst: boolean): string => {
    if (!part || part === part.toLowerCase() || part !== part.toUpperCase()) return part
    const low = part.toLowerCase()
    if (DESHOUT_STOPWORDS.has(low)) {
      return isFirst ? low.charAt(0).toUpperCase() + low.slice(1) : low
    }
    if (part.length <= 3 && /^\p{L}+$/u.test(part)) return part // sigla (EFT, TAI)
    return low.charAt(0).toUpperCase() + low.slice(1)
  }
  return s
    .split(' ')
    .map((word, wIdx) =>
      word.split("'").map((p, pIdx) => fixPart(p, wIdx === 0 && pIdx === 0)).join("'"),
    )
    .join(' ')
}

/** Normalizza il brand alla grafia ufficiale "OLLYlife" (non tocca ollylife.com). */
export function fixBrandCase(text: string): string {
  return text.replace(/\bollylife\b(?!\.)/gi, 'OLLYlife')
}

/** Extract YouTube or Vimeo video ID from a URL */
export function extractVideoId(
  url: string
): { platform: 'youtube' | 'vimeo'; id: string } | null {
  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, /embed/ID, /shorts/ID
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  )
  if (ytMatch) return { platform: 'youtube', id: ytMatch[1] }

  // Vimeo: vimeo.com/ID
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
  if (vimeoMatch) return { platform: 'vimeo', id: vimeoMatch[1] }

  return null
}

/** Build YouTube privacy-enhanced embed URL */
export function youtubeEmbedUrl(id: string) {
  return `https://www.youtube-nocookie.com/embed/${id}`
}

/** Build Vimeo embed URL */
export function vimeoEmbedUrl(id: string) {
  return `https://player.vimeo.com/video/${id}`
}

/** Truncate string to maxLength with ellipsis */
export function truncate(str: string, maxLength: number) {
  return str.length <= maxLength ? str : str.slice(0, maxLength - 1) + '…'
}

/** Format ISO date to Italian locale string */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

/** Generate URL-friendly slug from full name */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

/** Check if JWT is expired (with 60s buffer) */
export function isTokenExpired(expiryMs: number): boolean {
  return Date.now() >= expiryMs - 60_000
}

/** Prezzo in centesimi → «80,00 €» (stesso formato di formatPrice su www). */
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })
    .format((cents || 0) / 100)
}
